import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReviewCard, LoadingSkeleton } from './reviews/ReviewCard.jsx';
import { getDocumentLocale } from './reviews/reviewHelpers.js';
import {
  buildPageList,
  pageFromSearch,
  pageUrl,
} from './reviews/pagination.js';
import { discoverTotal, fetchDisplayPage } from './reviews/reviewsApi.js';

const PAGE_PARAM = 'page';

const readPageFromUrl = () => pageFromSearch(window.location.search, PAGE_PARAM);

const buildPageUrl = (page) =>
  pageUrl(window.location.pathname, window.location.search, page, PAGE_PARAM);

const PageLink = ({ page, isCurrent, onNavigate, children, label }) => (
  <a
    href={buildPageUrl(page)}
    aria-label={label}
    aria-current={isCurrent ? 'page' : undefined}
    onClick={(event) => {
      // Let modified clicks (new tab/window) behave normally.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
        return;
      }

      event.preventDefault();
      onNavigate(page);
    }}
    className={`inline-flex items-center justify-center min-w-10 h-10 px-3 rounded-md text-sm transition-colors ${
      isCurrent
        ? 'bg-dark-blue text-white font-semibold'
        : 'bg-gray-50 text-dark-blue hover:bg-light-blue'
    }`}
  >
    {children ?? page}
  </a>
);

/**
 * Numbered once the total is known; until then just previous/next, because we
 * cannot say how many pages there are.
 */
const Pagination = ({ currentPage, totalPages, hasNext, onNavigate, labels }) => {
  const hasPrevious = currentPage > 1;
  const knowsTotal = totalPages !== null;
  const canGoNext = knowsTotal ? currentPage < totalPages : hasNext;

  if (!hasPrevious && !canGoNext) {
    return null;
  }

  return (
    <nav className="mt-10 flex flex-col items-center gap-3" aria-label={labels.pagination}>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {hasPrevious && (
          <PageLink page={currentPage - 1} onNavigate={onNavigate} label={labels.previous}>
            {labels.previous}
          </PageLink>
        )}

        {knowsTotal ? (
          buildPageList(currentPage, totalPages).map((page, index) =>
            page === null ? (
              <span key={`gap-${index}`} className="px-1 text-gray-400" aria-hidden="true">
                &hellip;
              </span>
            ) : (
              <PageLink
                key={page}
                page={page}
                isCurrent={page === currentPage}
                onNavigate={onNavigate}
                label={`${labels.page} ${page}`}
              />
            )
          )
        ) : (
          <PageLink
            page={currentPage}
            isCurrent
            onNavigate={onNavigate}
            label={`${labels.page} ${currentPage}`}
          />
        )}

        {canGoNext && (
          <PageLink page={currentPage + 1} onNavigate={onNavigate} label={labels.next}>
            {labels.next}
          </PageLink>
        )}
      </div>

      <p className="text-xs text-gray-500">
        {labels.page} {currentPage}
        {knowsTotal ? ` / ${totalPages}` : ''}
      </p>
    </nav>
  );
};

const TRANSLATIONS = {
  nl: {
    pagination: 'Paginering reviews',
    previous: 'Vorige',
    next: 'Volgende',
    page: 'Pagina',
    reviews: 'reviews',
    error: 'De reviews konden niet worden geladen. Probeer het later opnieuw.',
    empty: 'Er zijn nog geen reviews.',
    average: 'gemiddeld',
  },
  en: {
    pagination: 'Reviews pagination',
    previous: 'Previous',
    next: 'Next',
    page: 'Page',
    reviews: 'reviews',
    error: 'The reviews could not be loaded. Please try again later.',
    empty: 'There are no reviews yet.',
    average: 'average',
  },
};

const ReviewsOverview = ({
  apiEndpoint = '/api/reviews',
  perPage = 20,
  locale = getDocumentLocale(),
  showSummary = true,
}) => {
  const labels = TRANSLATIONS[locale.slice(0, 2)] ?? TRANSLATIONS.nl;

  const [currentPage, setCurrentPage] = useState(readPageFromUrl);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [totalReviews, setTotalReviews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const listRef = useRef(null);
  const requestRef = useRef(0);

  const totalPages =
    totalReviews === null ? null : Math.max(1, Math.ceil(totalReviews / perPage));

  // Load the reviews for the current page. Only the newest request may write to
  // state, so quick clicking through pages cannot land out of order.
  useEffect(() => {
    const request = ++requestRef.current;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await fetchDisplayPage(apiEndpoint, currentPage, perPage);

        if (cancelled || request !== requestRef.current) {
          return;
        }

        setReviews(result.reviews);
        setAverageRating(result.averageRating);
        setHasNext(result.mayHaveMore);
      } catch (err) {
        if (cancelled || request !== requestRef.current) {
          return;
        }

        console.error('Error fetching reviews:', err);
        setError(err.message);
        setReviews([]);
      } finally {
        if (!cancelled && request === requestRef.current) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, currentPage, perPage]);

  // Find the total in the background so the pager can switch from
  // previous/next to real page numbers. Never blocks the first paint.
  useEffect(() => {
    let cancelled = false;

    discoverTotal(apiEndpoint)
      .then((total) => {
        if (!cancelled) {
          setTotalReviews(total);
        }
      })
      .catch((err) => {
        // Without a total the pager simply stays on previous/next.
        console.warn('Could not determine the number of reviews:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [apiEndpoint]);

  // Once the total is known, pull a deep-linked page back into range.
  useEffect(() => {
    if (totalPages !== null && currentPage > totalPages) {
      setCurrentPage(totalPages);
      window.history.replaceState({}, '', buildPageUrl(totalPages));
    }
  }, [currentPage, totalPages]);

  // Support the browser back/forward buttons.
  useEffect(() => {
    const onPopState = () => setCurrentPage(readPageFromUrl());

    window.addEventListener('popstate', onPopState);

    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((page) => {
    window.history.pushState({}, '', buildPageUrl(page));
    setCurrentPage(page);

    if (listRef.current) {
      const top =
        listRef.current.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  const showEmpty = !loading && !error && reviews.length === 0;

  return (
    <div ref={listRef}>
      {showSummary && totalReviews !== null && totalReviews > 0 && (
        <p className="text-sm text-gray-600 mb-6">
          {totalReviews} {labels.reviews}
          {averageRating ? ` · ${averageRating} / 5 ${labels.average}` : ''}
        </p>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: Math.min(perPage, 6) }, (_, index) => (
            <LoadingSkeleton key={index} />
          ))}
        </div>
      ) : error ? (
        <p className="text-red-500">{labels.error}</p>
      ) : showEmpty ? (
        <p className="text-gray-600">{labels.empty}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reviews.map((review, index) => (
            <ReviewCard
              key={`${review.author_name}-${review.time}-${index}`}
              review={review}
              locale={locale}
            />
          ))}
        </div>
      )}

      {!error && !showEmpty && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasNext={hasNext}
          onNavigate={navigate}
          labels={labels}
        />
      )}
    </div>
  );
};

export default ReviewsOverview;
